import { Shell } from '@/components/Shell';
import { EmptyRow } from '@/components/ui/EmptyState';

import { adminApi } from '@/lib/api';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { useState } from 'react';

export const Route = createFileRoute('/audit')({
  beforeLoad: async () => {
    try {
      const me = await adminApi.me();
      if (me.role !== 'admin') throw redirect({ to: '/login' });
    } catch {
      throw redirect({ to: '/login' });
    }
  },
  component: AuditPage,
});

function AuditPage() {
  const [action, setAction] = useState('');
  const [table, setTable] = useState('');
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['admin', 'audit', action, table],
    queryFn: () => adminApi.auditLog({ action: action || undefined, targetTable: table || undefined }),
  });
  const dateFmt = new Intl.DateTimeFormat('pt-PT', { dateStyle: 'short', timeStyle: 'medium' });
  const [expanded, setExpanded] = useState<string | null>(null);
  // Unfiltered sample feeds the filter dropdowns so operators pick real values
  // instead of guessing internal names like "booking.cancel".
  const { data: all } = useQuery({
    queryKey: ['admin', 'audit', 'options'],
    queryFn: () => adminApi.auditLog({}),
    staleTime: 60_000,
  });
  const distinct = (pick: (r: { action: string; targetTable: string }) => string, current: string) =>
    Array.from(new Set([...(all?.items ?? []).map(pick), ...(current ? [current] : [])])).sort();
  const actionOptions = distinct((r) => r.action, action);
  const tableOptions = distinct((r) => r.targetTable, table);
  const count = data?.paging.count ?? 0;

  return (
    <Shell page="AUDITORIA" sub={`REGISTO DE ALTERAÇÕES · ${count} ENTRADAS`}>
      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <select
          value={action}
          onChange={(e) => setAction(e.target.value)}
          style={inputStyle}
          aria-label="Acção"
        >
          <option value="">Todas as acções</option>
          {actionOptions.map((a) => (
            <option key={a} value={a}>
              {a}
            </option>
          ))}
        </select>
        <select
          value={table}
          onChange={(e) => setTable(e.target.value)}
          style={inputStyle}
          aria-label="Tabela"
        >
          <option value="">Todas as tabelas</option>
          {tableOptions.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </div>

      {/* Table */}
      <div style={{ background: '#fff', border: '1px solid var(--lv-line)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: 'var(--lv-night)' }}>
              {['QUANDO', 'QUEM', 'ACÇÃO', 'ALVO', ''].map((h) => (
                <th
                  key={h}
                  className="label"
                  style={{
                    color: 'var(--lv-brand-glow)',
                    padding: '12px 16px',
                    textAlign: 'left',
                    fontSize: 10,
                    fontWeight: 800,
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={5} style={{ padding: 20, color: 'var(--lv-gray-500)', fontSize: 12 }}>
                  A carregar…
                </td>
              </tr>
            )}
            {isError && (
              <tr>
                <td colSpan={5} style={{ padding: 20, color: 'var(--lv-err)', fontSize: 12 }}>
                  Não foi possível carregar o registo: {(error as Error).message}
                </td>
              </tr>
            )}
            {!isLoading && !isError && data?.items.length === 0 && (
              <EmptyRow colSpan={5} message="Sem entradas para este filtro." />
            )}
            {data?.items.map((r) => {
              const isOpen = expanded === r.id;

              const hasDiff = r.before != null || r.after != null;
              return (
                <>
                  <tr key={r.id} style={{ borderTop: '1px solid var(--lv-line)' }}>
                    <td
                      style={{
                        padding: '12px 16px',
                        fontSize: 11,
                        color: 'var(--lv-gray-500)',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {dateFmt.format(new Date(r.at))}
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: 12 }}>
                      {r.actorEmail ?? <span style={{ color: 'var(--lv-gray-500)' }}>—</span>}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <span
                        style={{
                          fontFamily: 'var(--lv-font-mono)',
                          fontSize: 12,
                          background: 'var(--lv-surface-soft)',
                          padding: '2px 7px',
                          border: '1px solid var(--lv-line)',
                        }}
                      >
                        {r.action}
                      </span>
                    </td>
                    <td
                      style={{
                        padding: '12px 16px',
                        fontFamily: 'var(--lv-font-mono)',
                        fontSize: 11,
                        color: 'var(--lv-gray-500)',
                      }}
                    >
                      {r.targetTable}/{r.targetId.slice(0, 8)}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                      {/* Entries written by the audit middleware carry no snapshots, so the
                          button used to open a panel reading "(null)" → "(null)" — which looks
                          like the log lost the data rather than never having captured it. */}
                      {hasDiff ? (
                        <button
                          type="button"
                          onClick={() => setExpanded(isOpen ? null : r.id)}
                          style={btnSmall}
                        >
                          {isOpen ? 'FECHAR' : 'DIFF'}
                        </button>
                      ) : (
                        <span style={{ fontSize: 11, color: 'var(--lv-gray-500)' }}>—</span>
                      )}
                    </td>
                  </tr>
                  {isOpen && (
                    <tr key={`${r.id}-diff`}>
                      <td
                        colSpan={5}
                        style={{
                          padding: '16px 20px',
                          background: 'var(--lv-surface-soft)',
                          borderTop: '1px solid var(--lv-line)',
                        }}
                      >
                        <div
                          style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                            gap: 12,
                            fontSize: 11,
                          }}
                        >
                          <Pre title="ANTES" data={r.before} />
                          <Pre title="DEPOIS" data={r.after} />
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              );
            })}
          </tbody>
        </table>

        {data && (
          <div
            style={{
              borderTop: '1px solid var(--lv-line)',
              padding: '12px 16px',
              fontSize: 12,
              color: 'var(--lv-gray-500)',
            }}
          >
            Mostrando {data.items.length} de {count}
          </div>
        )}
      </div>
    </Shell>
  );
}

function Pre({ title, data }: { title: string; data: unknown }) {
  return (
    <div>
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: 'var(--lv-gray-500)',
          marginBottom: 6,
        }}
      >
        {title}
      </div>
      <pre
        style={{
          margin: 0,
          padding: '10px 12px',
          background: '#fff',
          border: '1px solid var(--lv-line)',
          fontFamily: 'var(--lv-font-mono)',
          fontSize: 11,
          overflow: 'auto',
          maxHeight: 300,
          lineHeight: 1.5,
        }}
      >
        {data === null || data === undefined ? '(null)' : JSON.stringify(data, null, 2)}
      </pre>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  padding: '10px 12px',
  border: '1px solid var(--lv-line)',
  background: '#fff',
  fontSize: 13,
  minWidth: 220,
  borderRadius: 0,
};
const btnSmall: React.CSSProperties = {
  background: 'transparent',
  color: 'var(--lv-ink)',
  border: '1px solid var(--lv-line)',
  padding: '5px 10px',
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  cursor: 'pointer',
};
